import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import SignInRequired from '../components/SignInRequired';

// Recipe Modal Component
const RecipeModal = ({ isOpen, onClose, recipe }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-900">{recipe?.name || 'Recipe'}</h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          
          {recipe?.video_id && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">Source:</h4>
              <a 
                href={`https://youtube.com/watch?v=${recipe.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                Watch on YouTube
              </a>
            </div>
          )}
          
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Nutritional Info:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm bg-gray-50 p-3 rounded-lg">
              <div className="text-center">
                <div className="font-medium">{Math.round(recipe?.calories || 0)}</div>
                <div className="text-xs text-gray-500">Calories</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{Math.round(recipe?.protein || 0)}g</div>
                <div className="text-xs text-gray-500">Protein</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{Math.round(recipe?.carbs || 0)}g</div>
                <div className="text-xs text-gray-500">Carbs</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{Math.round(recipe?.fat || 0)}g</div>
                <div className="text-xs text-gray-500">Fat</div>
              </div>
            </div>
          </div>

          {recipe?.ingredients || recipe?.instructions ? (
            <>
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Ingredients:</h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {Array.isArray(recipe.ingredients) 
                    ? recipe.ingredients.map((ingredient, idx) => (
                        <li key={idx} className="text-sm">{ingredient}</li>
                      ))
                    : <li className="text-sm">No ingredients available</li>
                  }
                </ul>
              </div>

              {recipe.instructions && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Instructions:</h4>
                  <div className="prose max-w-none text-gray-700 text-sm whitespace-pre-line">
                    {recipe.instructions}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">
              Recipe details not available for this item.
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const SavedPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleViewRecipe = (meal) => {
    setSelectedRecipe(meal);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecipe(null);
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/meal-plans/my-plans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setPlans(response.data);
      } catch (error) {
        console.error('Error fetching meal plans:', error);
        toast.error('Failed to load meal plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const filteredPlans =
    activeTab === 'all'
      ? plans
      : plans.filter((plan) => {
          if (activeTab === 'weekly') {
            return plan.type === 'weekly' || plan.type === 'custom_weekly';
          }
          if (activeTab === 'daily') {
            return plan.type === 'daily' || plan.type === 'next_meal';
          }
          return plan.type === 'custom_weekly' ? activeTab === 'weekly' : plan.type === activeTab;
        });

  const deletePlan = async (planId) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${BACKEND_URL}/api/meal-plans/${planId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setPlans(plans.filter(plan => plan.id !== planId));
        toast.success('Meal plan deleted successfully');
      } catch (error) {
        console.error('Error deleting meal plan:', error);
        toast.error('Failed to delete meal plan');
      }
    }
  };

  const toggleFavorite = async (planId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/meal-plans/${planId}/favorite`,
        { is_favorite: !currentStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setPlans(plans.map(plan => 
        plan.id === planId 
          ? { ...plan, is_favorite: !currentStatus } 
          : plan
      ));
      
      toast.success(`Plan ${!currentStatus ? 'added to' : 'removed from'} favorites`);
    } catch (error) {
      console.error('Error updating favorite status:', error);
      toast.error('Failed to update favorite status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    
    let date;
    
    // Handle different date string formats
    if (typeof dateString === 'string') {
      // Check if it's in ISO format (from backend)
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } 
      // Handle other date formats if needed
      else {
        date = new Date(dateString);
      }
    } 
    // If it's already a Date object
    else if (dateString instanceof Date) {
      date = dateString;
    } 
    // If it's a timestamp
    else if (typeof dateString === 'number') {
      date = new Date(dateString);
    } 
    // Fallback to current date
    else {
      date = new Date();
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return 'Invalid date';
    }
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  const getTotalNutrients = (meals) => {
    // Handle both array and object formats
    let allMeals = [];
    
    if (Array.isArray(meals)) {
      // If it's an array, use it directly
      allMeals = meals.filter(meal => meal && typeof meal === 'object');
    } else if (typeof meals === 'object' && meals !== null) {
      // If it's an object with meal types, flatten all meals
      allMeals = Object.values(meals).flat().filter(meal => meal && typeof meal === 'object');
    }
    
    return allMeals.reduce((acc, meal) => ({
      calories: acc.calories + (Number(meal.calories) || 0),
      protein: acc.protein + (Number(meal.protein) || 0),
      carbs: acc.carbs + (Number(meal.carbs) || 0),
      fat: acc.fat + (Number(meal.fat) || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  // Utility to get total nutrition for any plan type
  const groupMealsByDate = (meals) => {
    if (!meals || !Array.isArray(meals)) return {};
    
    return meals.reduce((groups, meal) => {
      const date = meal.date || 'No date';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(meal);
      return groups;
    }, {});
  };

  const getPlanTotals = (plan) => {
    // Custom weekly plans (top-level fields)
    if (plan.total_calories != null) {
      return {
        calories: plan.total_calories,
        protein: plan.total_protein,
        carbs: plan.total_carbs,
        fat: plan.total_fat,
      };
    }
    // AI weekly plans
    if (plan.weekly_totals) {
      return plan.weekly_totals;
    }
    // AI daily plans
    if (plan.totals) {
      return plan.totals;
    }
    // Fallback: calculate from meals
    if (plan.meals) {
      return getTotalNutrients(plan.meals);
    }
    return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-white rounded-lg shadow p-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SignInRequired featureName="Saved Meal Plans">
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h4 className="heading-4">My Meal Plans</h4>
          <p className="text-gray-600">View and manage your saved meal plans</p>
        </div>

        {/* Recipe Modal */}
        <RecipeModal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          recipe={selectedRecipe} 
        />

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-4 font-medium text-sm ${activeTab === 'all' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              All Plans
            </button>
            <button
              onClick={() => setActiveTab('daily')}
              className={`px-6 py-4 font-medium text-sm ${activeTab === 'daily' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Daily Plans
            </button>
            <button
              onClick={() => setActiveTab('weekly')}
              className={`px-6 py-4 font-medium text-sm ${activeTab === 'weekly' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Weekly Plans
            </button>
          </div>

          <div className="p-6">
            {filteredPlans.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No {activeTab === 'all' ? '' : activeTab} plans found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {activeTab === 'all' 
                    ? 'You don\'t have any saved meal plans yet.'
                    : `You don't have any ${activeTab} plans saved.`}
                  }
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/meal-planner')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <svg
                      className="-ml-1 mr-2 h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Create New Plan
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPlans.map((plan) => {
                  const total = getPlanTotals(plan);

                  return (
                    <div key={plan.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 md:p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center">
                              <h3 className="text-lg font-medium text-gray-900">{plan.name || 'Untitled Plan'}</h3>
                              {plan.is_favorite && (
                                <span className="ml-2 text-yellow-400">
                                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {plan.type === 'daily' ? (
                                <span>{formatDate(plan.date || plan.created_at)}</span>
                              ) : (
                                <span>
                                  {plan.start_date || plan.created_at 
                                    ? `${formatDate(plan.start_date || plan.created_at)} - ${formatDate(plan.end_date || plan.created_at)}`
                                    : 'No date range'}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => toggleFavorite(plan.id, plan.is_favorite)}
                              className="p-1 text-gray-400 hover:text-yellow-400"
                              title={plan.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              {plan.is_favorite ? (
                                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => deletePlan(plan.id)}
                              className="p-1 text-gray-400 hover:text-red-500"
                              title="Delete plan"
                            >
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-500">Calories</p>
                            <p className="text-lg font-semibold text-gray-900">{Math.round(total.calories || 0)}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-500">Protein</p>
                            <p className="text-lg font-semibold text-gray-900">{Math.round(total.protein || 0)}g</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-500">Carbs</p>
                            <p className="text-lg font-semibold text-gray-900">{Math.round(total.carbs || 0)}g</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-500">Fat</p>
                            <p className="text-lg font-semibold text-gray-900">{Math.round(total.fat || 0)}g</p>
                          </div>
                        </div>

                        {plan.meals && plan.meals.length > 0 && (
                          <div className="mt-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-3 border-b pb-1">Meal Details</h4>
                            {Object.entries(groupMealsByDate(plan.meals)).map(([date, dateMeals]) => (
                              <div key={date} className="mb-6">
                                <h5 className="text-sm font-medium text-gray-700 mb-3 border-b pb-1">
                                  {date === 'No date' ? 'Meals' : `Meals for ${formatDate(date)}`}
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                  {dateMeals.map((meal, idx) => (
                                    meal && (
                                      <div key={`${date}-${idx}`} className="bg-white rounded-lg border border-gray-100 p-4 hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <h5 className="font-medium text-gray-900">
                                              {meal.name || `Meal ${idx + 1}`}
                                            </h5>
                                            <p className="text-sm text-gray-500 capitalize">
                                              {meal.meal_type || 'meal'}
                                            </p>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium text-gray-700">
                                              {Math.round(meal.calories || 0)} cal
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewRecipe(meal);
                                              }}
                                              className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                                            >
                                              View Recipe
                                            </button>
                                          </div>
                                        </div>
                                        
                                        <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                                          <div className="bg-gray-50 p-2 rounded">
                                            <div className="font-medium">{Math.round(meal.protein || 0)}g</div>
                                            <div className="text-gray-500">Protein</div>
                                          </div>
                                          <div className="bg-gray-50 p-2 rounded">
                                            <div className="font-medium">{Math.round(meal.carbs || 0)}g</div>
                                            <div className="text-gray-500">Carbs</div>
                                          </div>
                                          <div className="bg-gray-50 p-2 rounded">
                                            <div className="font-medium">{Math.round(meal.fat || 0)}g</div>
                                            <div className="text-gray-500">Fat</div>
                                          </div>
                                        </div>
                                        
                                        {meal.ingredients && (
                                          <div className="mt-3">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Key Ingredients:</div>
                                            <div className="flex flex-wrap gap-1">
                                              {(Array.isArray(meal.ingredients) 
                                                ? meal.ingredients.slice(0, 3) 
                                                : [meal.ingredients].slice(0, 3)
                                              ).map((ingredient, i) => (
                                                <span 
                                                  key={i} 
                                                  className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                                                >
                                                  {ingredient}
                                                </span>
                                              ))}
                                              {((Array.isArray(meal.ingredients) && meal.ingredients.length > 3) || 
                                                (typeof meal.ingredients === 'string' && meal.ingredients.split(',').length > 3)) && (
                                                <span className="text-xs text-gray-500 self-center">
                                                  +{Array.isArray(meal.ingredients) ? meal.ingredients.length - 3 : meal.ingredients.split(',').length - 3} more
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </SignInRequired>
  );
};

export default SavedPlansPage;
